import React from 'react'
import { WidthProps } from 'styled-system'

import { Box } from '@ally/metronome-ui'
import { BadWords } from '@ally/use-validation'
import { invalidStrings, CustomTextInputProps, TextInput } from '@ally/forms'
import { toSentenceCase } from '@ally/ally-login'

/*
  NOTE: this component is intended to be temporary ONLY.
  It will be replaced by the exported <Name /> component from @ally/metronome-ui AFTER upgrading to v22+
  //
  The reason for this workaround is because the current state of @ally/ally-login is utilizing the <Name /> instance from @ally/forms
  @ally/forms was deprecated and migrated into the metronome repo in v22
  //
  That means updates to the component must happen either within v22 (which can't then be consumed in ally-login) 
    or inside of libraries_old which can't happen due to critical vulnerabilities within the CI/CD pipeline 
    which can only be resolved by upgrading to metronome v22 which is not an option
  //
  The ONLY change implemented here (as of 2022-08-03) was increasing the max char limit for last name from 20 to 30
*/

interface NameInputProps extends CustomTextInputProps {
  variant: 'First Name' | 'Last Name'
  autoComplete?: 'given-name' | 'family-name' | 'off'
  label?: string
  width?: WidthProps['width']
  required?: boolean
  badWordsObject?: BadWords
  contextualHelpContent?: string
}

export const defaultValidate = (
  variant: string,
  value: string,
  required: boolean,
  badWordsObject?: BadWords,
  NAME_PATTERN = /^[A-Za-z'\-\s]+$/,
): string | void => {
  let variantMaxLength
  let variantName

  // For max lengths see PersonNameRepresentation:
  // https://bitbucket.int.ally.com/projects/DAO/repos/dao-applications/browse/dao-applications-v1.yaml#1641
  switch (variant) {
    case 'First Name':
      variantMaxLength = 17
      variantName = 'first name'
      break
    case 'Last Name':
      variantMaxLength = 30
      variantName = 'last name'
      break
    default:
      variantMaxLength = 20
      variantName = 'name'
  }

  // if the input is not required then an empty field should never show a validation error
  if (!required && !value) {
    return undefined
  }

  if (!value) {
    return `Enter ${variantName} to continue.`
  }

  if (value.length > variantMaxLength) {
    return `Your ${variantName} can't contain more than ${variantMaxLength} characters.`
  }

  if (!NAME_PATTERN.test(value)) {
    return `Enter a valid ${variantName}.`
  }

  if (badWordsObject) {
    const invalidStringsValidator = invalidStrings.validator(badWordsObject)

    if (!invalidStringsValidator(value)) {
      return invalidStrings.errorMessage
    }
  }

  return undefined
}

const CustomName: React.FC<NameInputProps> = ({
  autoComplete = 'off',
  className,
  name,
  variant,
  label,
  required = false,
  validate,
  width,
  badWordsObject,
  contextualHelpContent,
  ...props
}) => {
  const fallback = (value: string): string | void =>
    defaultValidate(variant, value, required, badWordsObject)

  return (
    <Box className={className} width={width}>
      <TextInput
        labelContent={toSentenceCase(variant)}
        data-testid={name}
        required={required}
        autoComplete={autoComplete}
        isPrivate
        inputType="text"
        name={name}
        validate={validate || fallback}
        contextualHelp={{ content: contextualHelpContent, variant: 'stacked' }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    </Box>
  )
}

export default CustomName
